import React, { useState, useEffect } from 'react'
import { withTranslation } from 'react-i18next'
import { gql, useLazyQuery } from '@apollo/client'
import {
  Container,
  Grid,
  Box,
  Typography,
  TextField,
  Button,
  Tabs,
  Tab,
  Tooltip
} from '@mui/material'
import { Icon } from '@iconify/react'
import DataTable from 'react-data-table-component'
import { Line } from 'react-chartjs-2'
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip as ChartTooltip,
  Legend,
  Filler
} from 'chart.js'

import Header from '../components/Headers/Header'
import CustomLoader from '../components/Loader/CustomLoader'
import TableHeader from '../components/TableHeader'
import useGlobalStyles from '../utils/globalStyles'
import { customStyles } from '../utils/tableForAnalyticsDashboard'
import { AnalyzeCohorts, GetMonthlyOrderMetrics, getorderFilter } from '../apollo'

/* ---------------- GraphQL Queries ---------------- */
const GET_MONTHLY_ORDER_METRICS = gql`${GetMonthlyOrderMetrics}`
const GET_ORDER_FILTER = gql`${getorderFilter}`
const ANALYZE_COHORTS = gql`${AnalyzeCohorts}`

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  ChartTooltip,
  Legend,
  Filler
)

/* ---------------- Reusable KPI Card ---------------- */
const StatCard = ({ label, value, color = '#1976d2' }) => (
  <Box
    sx={{
      background: '#fff',
      borderRadius: 2,
      padding: 2,
      boxShadow: '0px 2px 12px rgba(0,0,0,0.1)',
      textAlign: 'center',
      transition: 'transform 0.2s',
      '&:hover': { transform: 'translateY(-4px)' }
    }}
  >
    <Typography variant="caption" color="textSecondary">
      {label}
    </Typography>
    <Typography variant="h6" fontWeight={600} sx={{ color }}>
      {value}
    </Typography>
  </Box>
)

/* ---------------- Main Page ---------------- */
const AnalyticsDashboard = ({ t }) => {
  const globalClasses = useGlobalStyles()
  const currentMonth = new Date().toISOString().slice(0, 7)

  const [formData, setFormData] = useState({
    startDate: currentMonth,
    endDate: currentMonth
  })

  const [monthlyData, setMonthlyData] = useState([])
  const [dailyData, setDailyData] = useState([])
  const [cohortData, setCohortData] = useState([])
  const [tab, setTab] = useState(0)
  const [viewMode, setViewMode] = useState('monthly')

  const buildMonthRange = function(monthValue, endOfMonth) {
    if (!monthValue) return null
    const date = new Date(monthValue + '-01T00:00:00.000Z')
    if (endOfMonth) {
      const endDate = new Date(date.getFullYear(), date.getMonth() + 1, 0, 23, 59, 59, 999)
      return endDate.toISOString()
    }
    return new Date(date.getFullYear(), date.getMonth(), 1, 0, 0, 0, 0).toISOString()
  }

  const [fetchMonthlyMetrics, { loading: monthlyLoading }] = useLazyQuery(
    GET_MONTHLY_ORDER_METRICS,
    {
      onCompleted: function(d) {
        if (d && d.getMonthlyOrderMetrics) {
          setMonthlyData(d.getMonthlyOrderMetrics)
        } else {
          setMonthlyData([])
        }
      }
    }
  )

  const [fetchCohorts, { loading: cohortLoading }] = useLazyQuery(
    ANALYZE_COHORTS,
    {
      onCompleted: function(d) {
        if (d && d.analyzeCohorts) {
          setCohortData(d.analyzeCohorts)
        } else {
          setCohortData([])
        }
      }
    }
  )

  const [fetchDailyOrders, { loading: dailyLoading }] = useLazyQuery(
    GET_ORDER_FILTER,
    {
      fetchPolicy: 'no-cache',
      onCompleted: function(d) {
        if (d && d.getAllOrdersByFilter && d.getAllOrdersByFilter.orders) {
          var grouped = {}
          d.getAllOrdersByFilter.orders.forEach(function(order) {
            var orderStatus = Number(order.orderStatus)
            if (orderStatus !== 7 && Number(order.status) !== 7) return

            var orderDate = order.orderDate || order.deliveryDate || order.createdAt
            if (!orderDate) return
            var dateKey = new Date(orderDate).toISOString().split('T')[0]
            if (!grouped[dateKey]) {
              grouped[dateKey] = { date: dateKey, orders: 0, revenue: 0 }
            }
            grouped[dateKey].orders += 1
            grouped[dateKey].revenue += Number(order.orderAmount || 0)
          })
          setDailyData(Object.values(grouped).sort(function(a, b) {
            return new Date(a.date) - new Date(b.date)
          }))
        } else {
          setDailyData([])
        }
      }
    }
  )

  /* -------- Auto-fetch on page load -------- */
  useEffect(function() {
    fetchMonthlyMetrics({ variables: formData })
    fetchCohorts({ variables: formData })
    fetchDailyOrders({
      variables: {
        filterInput: {
          startDate: buildMonthRange(formData.startDate, false),
          endDate: buildMonthRange(formData.endDate, true)
        },
        limit: 100000,
        skip: 0
      }
    })
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const handleFetch = function() {
    if (!formData.startDate || !formData.endDate) {
      alert('Select dates')
      return
    }
    fetchMonthlyMetrics({ variables: formData })
    fetchCohorts({ variables: formData })
    if (viewMode === 'daily') {
      fetchDailyOrders({
        variables: {
          filterInput: {
            startDate: buildMonthRange(formData.startDate, false),
            endDate: buildMonthRange(formData.endDate, true)
          },
          limit: 100000,
          skip: 0
        }
      })
    }
  }
  /* ---------------- CSV Download ---------------- */
    const handleDownloadCSV = function () {
      var rows = []
      var headers = ''
      var csv = ''

      if (tab === 0) {
        // Monthly Metrics CSV
        if (!monthlyData || monthlyData.length === 0) return
        headers = 'Month,Orders,Revenue,First Buyers,Monthly Buyers,Repeat Buyers,Installs,Conversion %'
        rows = monthlyData.map(function (r) {
          return [
            r.orderedMonth,
            r.totalOrders,
            r.orderAmount,
            r.firstOrderCustomers,
            r.uniqueCustomers,
            r.repeatedCustomers,
            r.totalInstalls,
            (r.conversionRate * 100).toFixed(2)
          ].join(',')
        })
      } else {
        // Cohort Analysis CSV
        if (!cohortTableData || cohortTableData.length === 0) return
        headers = 'Install Month,Order Month,Installs,Active Users,Orders,Revenue,Conversion %'
        rows = cohortTableData.map(function (r) {
          return [
            r.installedMonth,
            r.orderedMonth,
            r.installs,
            r.activeCustomers,
            r.totalOrders,
            r.orderAmount,
            (r.conversionRate * 100).toFixed(2)
          ].join(',')
        })
      }

      csv = headers + '\n' + rows.join('\n')

      var blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
      var url = URL.createObjectURL(blob)

      var a = document.createElement('a')
      a.href = url
          var start = formData.startDate || 'NA'
    var end = formData.endDate || 'NA'
    var range = start + '_to_' + end

    a.download =
      tab === 0
        ? 'Monthly_Metrics_' + range + '.csv': 'Cohort_Analysis_' + range + '.csv'
      a.click()
      URL.revokeObjectURL(url)
    }

  /* ---------------- KPI Aggregation ---------------- */
  const summary = {
    totalOrders: Math.round(monthlyData.reduce(function(sum, m) { return sum + (m.totalOrders || 0) }, 0)),
    orderAmount: parseFloat(monthlyData.reduce(function(sum, m) { return sum + (m.orderAmount || 0) }, 0).toFixed(2)),
    newCustomers: Math.round(monthlyData.reduce(function(sum, m) { return sum + (m.firstOrderCustomers || 0) }, 0)),
    orderingCustomers: Math.round(monthlyData.reduce(function(sum, m) { return sum + (m.uniqueCustomers || 0) }, 0)),
    totalInstalls: Math.round(monthlyData.reduce(function(sum, m) { return sum + (m.totalInstalls || 0) }, 0))
  }

  var conversionRate = 0
  if (summary.totalInstalls > 0) {
    conversionRate = ((summary.newCustomers / summary.totalInstalls) * 100).toFixed(2)
  }

  const chartSource = viewMode === 'daily' ? dailyData : monthlyData
  const isLargeChart = chartSource.length > 60
  const chartPointRadius = isLargeChart ? 0 : 4
  const chartPointHoverRadius = isLargeChart ? 5 : 6

  const chartData = {
    labels: chartSource.map(function(item) {
      return viewMode === 'daily' ? (item.date || 'N/A') : (item.orderedMonth || 'N/A')
    }),
    datasets: [
      {
        label: 'Orders',
        data: chartSource.map(function(item) {
          return viewMode === 'daily' ? Number(item.orders || 0) : Number(item.totalOrders || 0)
        }),
        borderColor: '#1976d2',
        backgroundColor: 'rgba(25, 118, 210, 0.15)',
        yAxisID: 'yOrders',
        tension: 0.3,
        fill: true,
        pointRadius: chartPointRadius,
        pointHoverRadius: chartPointHoverRadius
      },
      {
        label: 'Revenue',
        data: chartSource.map(function(item) {
          return viewMode === 'daily' ? Number(item.revenue || 0) : Number(item.orderAmount || 0)
        }),
        borderColor: '#388e3c',
        backgroundColor: 'rgba(56, 142, 60, 0.12)',
        yAxisID: 'yRevenue',
        tension: 0.3,
        fill: true,
        pointRadius: chartPointRadius,
        pointHoverRadius: chartPointHoverRadius
      }
    ]
  }

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    animation: isLargeChart ? false : undefined,
    normalized: true,
    interaction: {
      mode: 'index',
      intersect: false
    },
    plugins: {
      legend: {
        position: 'top'
      },
      tooltip: {
        callbacks: {
          label: function(context) {
            return context.dataset.label === 'Revenue'
              ? 'Revenue: ₹' + Number(context.parsed.y).toFixed(2)
              : 'Orders: ' + Number(context.parsed.y).toLocaleString()
          }
        }
      }
    },
    scales: {
      x: {
        grid: {
          display: false
        },
        ticks: {
          autoSkip: true,
          maxTicksLimit: isLargeChart ? 12 : 24,
          maxRotation: isLargeChart ? 45 : 0,
          minRotation: isLargeChart ? 45 : 0
        },
        title: {
          display: true,
          text: viewMode === 'daily' ? 'Date' : 'Month'
        }
      },
      yOrders: {
        type: 'linear',
        position: 'left',
        grid: {
          color: 'rgba(0,0,0,0.06)'
        },
        title: {
          display: true,
          text: 'Orders'
        }
      },
      yRevenue: {
        type: 'linear',
        position: 'right',
        grid: {
          drawOnChartArea: false
        },
        title: {
          display: true,
          text: 'Revenue (₹)'
        }
      }
    }
  }

  /* ---------------- Table Columns ---------------- */
  const monthlyColumns = [
    { name: 'Month', selector: function(row) { return row.orderedMonth }, sortable: true },
    { name: 'Orders', selector: function(row) { return Math.round(row.totalOrders) }, sortable: true },
    { name: 'Revenue', selector: function(row) { return '₹' + parseFloat(row.orderAmount).toFixed(2) }, sortable: true },
    {
      name: 'Unique Customers',
      selector: function(row) { return row.uniqueCustomers },
      sortable: true,
      cell: function(row) {
        return React.createElement(
          Tooltip,
          { title: 'How many different customers placed ≥1 order' },
          React.createElement('span', null, Math.round(row.uniqueCustomers))
        )
      }
    },
    {
      name: 'First-Order Customers',
      selector: function(row) { return row.firstOrderCustomers },
      sortable: true,
      cell: function(row) {
        return React.createElement(
          Tooltip,
          { title: 'How many placed their first-ever order' },
          React.createElement('span', null, Math.round(row.firstOrderCustomers))
        )
      }
    },
    {
      name: 'Repeated Customers',
      selector: function(row) { return row.repeatedCustomers },
      sortable: true,
      cell: function(row) {
        return React.createElement(
          Tooltip,
          { title: 'How many placed >1 order within the month' },
          React.createElement('span', null, Math.round(row.repeatedCustomers))
        )
      }
    },
    { name: 'Total Installs', selector: function(row) { return Math.round(row.totalInstalls) }, sortable: true },
    {
      name: 'Conversion %',
      selector: function(row) { return row.conversionRate },
      cell: function(row) {
        var color = row.conversionRate >= 0.2 ? 'green' : 'red'
        return React.createElement(Typography, { sx: { color: color } }, (row.conversionRate * 100).toFixed(2) + '%')
      },
      sortable: true
    }
  ]

  /* -------- Cohort Flatten -------- */
  var cohortTableData = []
    if (cohortData && cohortData.length > 0) {
      for (var i = 0; i < cohortData.length; i++) {
        var cohort = cohortData[i]
        var installs = cohort.totalInstallperMonth || 0

        if (cohort.timePeriods && cohort.timePeriods.length > 0) {
          for (var j = 0; j < cohort.timePeriods.length; j++) {
            var tp = cohort.timePeriods[j]

            // TEMP: Cohort conversion = first order / total installs
            var conversionRate = 0
            if (installs > 0) {
              conversionRate = (tp.firstOrderCustomers || 0) / installs
            }

            cohortTableData.push({
              installedMonth: cohort.installedMonth,
              orderedMonth: tp.orderedMonth,
              installs: installs,
              activeCustomers: tp.activeCustomers,
              totalOrders: tp.totalOrders,
              orderAmount: tp.orderAmount,

              // 🔥 override backend value
              conversionRate: conversionRate,

              ordersGrowthPercent: tp.ordersGrowthPercent,
              amountGrowthPercent: tp.amountGrowthPercent
            })
          }
        }
      }
    }


  const cohortColumns = [
    { name: 'Install Mo', selector: function(row) { return row.installedMonth } },
    { name: 'Order Mo', selector: function(row) { return row.orderedMonth } },
    { name: 'Installs', selector: function(row) { return Math.round(row.installs) } },
    { name: 'Active Customers', selector: function(row) { return Math.round(row.activeCustomers) } },
    { name: 'Orders', selector: function(row) { return Math.round(row.totalOrders) } },
    { name: 'Revenue', selector: function(row) { return '₹' + parseFloat(row.orderAmount).toFixed(2) } },
    {
      name: 'Conversion %',
      selector: function(row) { return row.conversionRate },
      cell: function(row) {
        var color = row.conversionRate >= 0.2 ? 'green' : 'red'
        return React.createElement(Typography, { sx: { color: color } }, (row.conversionRate * 100).toFixed(2) + '%')
      }
    },
    {
      name: 'Order Gr %',
      selector: function(row) { return row.ordersGrowthPercent },
      cell: function(row) { return parseFloat(row.ordersGrowthPercent || 0).toFixed(2) + '%' }
    },
    {
      name: 'Rev Gr %',
      selector: function(row) { return row.amountGrowthPercent },
      cell: function(row) { return parseFloat(row.amountGrowthPercent || 0).toFixed(2) + '%' }
    }
  ]

  /* ---------------- UI ---------------- */
  return (
    <>
      <Header />
      <Container className={globalClasses.flex}>
        {/* Filters Card */}
        <Box sx={{ mb: 3, p: 2, backgroundColor: '#f5f5f5', borderRadius: 2 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={3}>
              <TextField
                type="month"
                label="Start Month"
                InputLabelProps={{ shrink: true }}
                InputProps={{ style: { color: '#000' } }}
                fullWidth
                value={formData.startDate}
                onChange={function(e){ setFormData({ startDate: e.target.value, endDate: formData.endDate }) }}
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField
                type="month"
                label="End Month"
                InputLabelProps={{ shrink: true }}
                InputProps={{ style: { color: '#000' } }}
                fullWidth
                value={formData.endDate}
                onChange={function(e){ setFormData({ startDate: formData.startDate, endDate: e.target.value }) }}
              />
            </Grid>
            <Grid item xs={12} sm={2}>
              <Button
                variant="contained"
                color="primary"
                startIcon={React.createElement(Icon, { icon: 'ic:round-search' })}
                fullWidth
                onClick={handleFetch}
              >
                Fetch
              </Button>
            </Grid>
            <Grid item xs={12} sm={2}>
              <Button
                variant="outlined"
                color="secondary"
                fullWidth
                onClick={function(){ setFormData({ startDate: currentMonth, endDate: currentMonth }) }}
              >
                Reset
              </Button>
            </Grid>
            <Grid item xs={12} sm={2}>
              <Button
                variant="outlined"
                color="success"
                fullWidth
                startIcon={React.createElement(Icon, { icon: 'ic:round-download' })}
                onClick={handleDownloadCSV}
              >
                Download CSV
              </Button>
            </Grid>
          </Grid>
        </Box>

        {/* KPI Cards */}
        <Grid container spacing={2} sx={{ mb: 4 }}>
          <Grid item xs={12} md={2.4}>
            <StatCard label="Total Orders" value={summary.totalOrders} color="#1976d2" />
          </Grid>
          <Grid item xs={12} md={2.4}>
            <StatCard label="Revenue" value={'₹' + summary.orderAmount} color="#388e3c" />
          </Grid>
          <Grid item xs={12} md={2.4}>
            <StatCard label="First-Order Customers" value={summary.newCustomers} color="#f57c00" />
          </Grid>
          <Grid item xs={12} md={2.4}>
            <StatCard label="Unique Customers" value={summary.orderingCustomers} color="#1976d2" />
          </Grid>
          <Grid item xs={12} md={2.4}>
            <StatCard label="Total Installs" value={summary.totalInstalls} color="#7b1fa2" />
          </Grid>
          <Grid item xs={12} md={2.4}>
            <StatCard label="Conversion %" value={parseFloat(conversionRate).toFixed(2) + '%'} color="#d32f2f" />
          </Grid>
        </Grid>

        <Box sx={{ mb: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Button
            variant={viewMode === 'monthly' ? 'contained' : 'outlined'}
            color="primary"
            onClick={function(){ setViewMode('monthly') }}
          >
            Monthly
          </Button>
          <Button
            variant={viewMode === 'daily' ? 'contained' : 'outlined'}
            color="secondary"
            onClick={function(){ setViewMode('daily'); handleFetch() }}
          >
            Daily
          </Button>
        </Box>

        {(viewMode === 'daily' ? dailyData.length > 0 : monthlyData.length > 0) && (
          <Box sx={{ mb: 4, p: 2, backgroundColor: '#fff', borderRadius: 2, boxShadow: '0px 2px 12px rgba(0,0,0,0.08)' }}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
              {viewMode === 'daily' ? 'Daily Orders & Revenue Trend' : 'Monthly Orders & Revenue Trend'}
            </Typography>
            <Box sx={{ height: 340 }}>
              {dailyLoading || monthlyLoading ? <CustomLoader /> : <Line data={chartData} options={chartOptions} />}
            </Box>
          </Box>
        )}

        {/* Tabs for Metrics */}
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
          <Tabs value={tab} onChange={function(e, val){ setTab(val) }}>
            <Tab label="Monthly Metrics" />
            <Tab label="Cohort Analysis" />
          </Tabs>
        </Box>

        {/* Data Tables */}
        {tab === 0 && (monthlyLoading ? (
          <CustomLoader />
        ) : (
          <DataTable
            title={React.createElement(TableHeader, { title: 'Monthly Order Metrics' })}
            columns={monthlyColumns}
            data={monthlyData}
            customStyles={customStyles}
            pagination
            highlightOnHover
            striped
          />
        ))}

        {tab === 1 && (cohortLoading ? (
          <CustomLoader />
        ) : (
          <DataTable
            title={React.createElement(TableHeader, { title: 'Cohort Analysis' })}
            columns={cohortColumns}
            data={cohortTableData}
            customStyles={customStyles}
            pagination
            highlightOnHover
            striped
          />
        ))}
      </Container>
    </>
  )
}
export default withTranslation()(AnalyticsDashboard)
